import React, { Suspense, createContext, lazy, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Paper, Snackbar, Tooltip, Typography } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RemoveIcon from '@mui/icons-material/Remove';
import { useLocation, useNavigate } from 'react-router-dom';
import T, { useAutoTranslate } from '../common/T';

const MeetingDirectMediaRoom = lazy(() => import('./MeetingDirectMediaRoom'));

const BRAND = '#32cd32';
const STORAGE_KEY = 'erp-active-meeting';
// Doubled selector: the ERP's global button styles would otherwise darken these on the dark bar.
const WINDOW_BUTTON = { '&&': { color: '#f8fafc', bgcolor: 'rgba(148,163,184,0.18)', width: 30, height: 30 },
  '&&:hover': { bgcolor: 'rgba(148,163,184,0.35)' }, '&& svg, && svg path': { color: '#f8fafc', fill: '#f8fafc' } };
const ActiveMeetingContext = createContext(null);

export const useActiveMeeting = () => useContext(ActiveMeetingContext);

function currentUser() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return '';
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return String(JSON.parse(atob(part)).sub || '');
  } catch {
    return '';
  }
}

function readStored() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return stored?.meetingId && stored.user && stored.user === currentUser() ? stored : null;
  } catch {
    return null;
  }
}

/**
 * The running meeting belongs to the ERP, not to the page it was started on.
 *
 * The room is mounted here, above every route, once. Its screen share, voices
 * and connections therefore survive going to any other part of the ERP. Its one
 * host element is moved between the Meetings page and the floating window: a
 * move keeps the room alive, where showing it in a second place would rebuild it.
 */
export function ActiveMeetingProvider({ children }) {
  const at = useAutoTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const [call, setCall] = useState(readStored);
  const [slot, setSlot] = useState(null);
  const [windowVisible, setWindowVisible] = useState(true);
  const [notice, setNotice] = useState('');
  const [indicators, setIndicators] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [host] = useState(() => {
    const element = document.createElement('div');
    element.style.width = '100%';
    return element;
  });
  const windowBody = useRef(null);
  const leftRef = useRef(false);
  const slotRef = useRef(null);
  slotRef.current = slot;
  const callRef = useRef(call);
  callRef.current = call;

  useEffect(() => {
    try {
      if (call) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(call));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* blocked storage only costs surviving a reload */ }
  }, [call]);

  const endCall = useCallback(meetingId => {
    setCall(current => (!meetingId || current?.meetingId === meetingId ? null : current));
    leftRef.current = false;
    setWindowVisible(true);
  }, []);

  const startCall = useCallback(details => {
    setCall(current => {
      if (current && current.meetingId !== details.meetingId) return current;
      const next = { ...details, user: currentUser() };
      return current && Object.keys(next).every(key => current[key] === next[key]) ? current : next;
    });
  }, []);

  useLayoutEffect(() => {
    const target = slot || windowBody.current;
    if (call && target && host.parentNode !== target) target.appendChild(host);
  }, [call, host, slot]);

  // Signing out, or somebody else signing in, ends the call with its devices.
  useEffect(() => {
    const check = () => {
      if (callRef.current && callRef.current.user !== currentUser()) endCall();
    };
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, [endCall, location.pathname]);

  const releaseSlot = useCallback(element => {
    setSlot(current => (current === element ? null : current));
    // Leaving the page is when the window has to say that the meeting goes on.
    setWindowVisible(true);
    // Somebody who already left the meeting has nothing to carry along.
    if (leftRef.current) endCall();
  }, [endCall]);

  const onLeftChange = useCallback(value => {
    leftRef.current = value;
    if (value && !slotRef.current) endCall();
  }, [endCall]);

  /** Access could not be proven. A changed policy is not the end of a meeting. */
  const onAccessLost = useCallback(async () => {
    const active = callRef.current;
    if (!active) return;
    try {
      const { getMeetingContext } = await import('../../api/communicationApi');
      const room = await getMeetingContext(active.meetingId, { timeout: 10000 });
      if (room?.state === 'live' && room.policy_revision !== active.policyRevision) {
        setCall(current => (current?.meetingId === active.meetingId
          ? { ...current, policyRevision: room.policy_revision } : current));
        return;
      }
    } catch { /* treated as the end below */ }
    if (!slotRef.current) {
      endCall(active.meetingId);
      setNotice('The meeting has ended.');
    }
  }, [endCall]);

  const openMeeting = useCallback(() => {
    const active = callRef.current;
    if (!active) return;
    navigate(`/communication?tab=meetings&meeting=${encodeURIComponent(active.meetingId)}`);
    window.dispatchEvent(new CustomEvent('erp-open-meeting', { detail: active.meetingId }));
  }, [navigate]);

  const startDrag = event => {
    if (event.button !== 0 || event.target.closest('button')) return;
    const from = { x: event.clientX - offset.x, y: event.clientY - offset.y };
    const move = moved => setOffset({
      x: Math.min(0, Math.max(moved.clientX - from.x, 340 - window.innerWidth)),
      y: Math.min(0, Math.max(moved.clientY - from.y, 120 - window.innerHeight)),
    });
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  const floating = Boolean(call && !slot);
  const value = useMemo(() => ({
    call, floating, windowVisible, setWindowVisible, startCall, endCall, setSlot, releaseSlot, openMeeting, setIndicators,
  }), [call, endCall, floating, openMeeting, releaseSlot, startCall, windowVisible]);

  return <ActiveMeetingContext.Provider value={value}>
    {children}
    {call && <Paper elevation={12} role="complementary" aria-label={at('Running meeting')}
      data-meeting-window="true" sx={{
        position: 'fixed', right: 16, bottom: 16, zIndex: 1250, width: 'min(460px, calc(100vw - 32px))',
        maxHeight: 'calc(100vh - 32px)', overflow: 'auto', borderRadius: 3, bgcolor: '#0b1220',
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        display: floating && windowVisible ? 'block' : 'none',
      }}>
      <Box onPointerDown={startDrag} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
        cursor: 'move', touchAction: 'none', borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BRAND, flexShrink: 0 }} />
        <Typography variant="subtitle2" noWrap sx={{ color: '#f8fafc', fontWeight: 700, flex: 1, minWidth: 0 }}>
          {call.meetingTitle}
        </Typography>
        <Tooltip title={at('Open meeting')}>
          <IconButton size="small" aria-label={at('Open meeting')} onClick={openMeeting} sx={WINDOW_BUTTON}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={at('Hide window. The meeting continues.')}>
          <IconButton size="small" aria-label={at('Hide window. The meeting continues.')}
            onClick={() => setWindowVisible(false)} sx={WINDOW_BUTTON}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box ref={windowBody} />
    </Paper>}
    {floating && !windowVisible && indicators === 0 && <Button variant="contained" onClick={() => setWindowVisible(true)}
      sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1250, bgcolor: BRAND, color: '#06240a', fontWeight: 700,
        '&:hover': { bgcolor: '#2ab52a' } }}>
      <T>Show meeting</T>
    </Button>}
    {call && createPortal(<Suspense fallback={null}>
      <MeetingDirectMediaRoom key={call.meetingId} meetingId={call.meetingId} policyRevision={call.policyRevision}
        mediaEnabled={call.mediaEnabled} meetingTitle={call.meetingTitle} selfName={call.selfName}
        transcriptEnabled={call.transcriptEnabled} floating={floating}
        onLeftChange={onLeftChange} onAccessLost={onAccessLost} />
    </Suspense>, host)}
    <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice('')} message={notice ? at(notice) : ''} />
  </ActiveMeetingContext.Provider>;
}

ActiveMeetingProvider.propTypes = { children: PropTypes.node };

/** The way back to a hidden window, in the top bar of every ERP page. */
export function MeetingCallIndicator() {
  const at = useAutoTranslate();
  const active = useActiveMeeting();
  const setIndicators = active?.setIndicators;
  useEffect(() => {
    if (!setIndicators) return undefined;
    setIndicators(count => count + 1);
    return () => setIndicators(count => count - 1);
  }, [setIndicators]);
  if (!active?.floating) return null;
  const shown = active.windowVisible;
  return <Tooltip title={shown ? at('Hide meeting window') : at('Show meeting window')}>
    <Button size="small" variant={shown ? 'outlined' : 'contained'} aria-pressed={shown}
      onClick={() => active.setWindowVisible(!shown)}
      sx={{ whiteSpace: 'nowrap', fontWeight: 700, textTransform: 'none', borderRadius: 5, flexShrink: 0,
        borderColor: BRAND, color: shown ? '#14631f' : '#06240a', bgcolor: shown ? 'transparent' : BRAND,
        '&:hover': { bgcolor: shown ? 'rgba(50,205,50,0.1)' : '#2ab52a', borderColor: BRAND } }}>
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: shown ? BRAND : '#06240a', mr: 0.75 }} />
      <T>In meeting</T>
    </Button>
  </Tooltip>;
}
